import { useMemo, useState } from 'react';
import { Minimize2, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { formatXml, minifyXml, type XmlResult } from '../lib/xml';
import { DualPaneFormatter } from '../components/DualPaneFormatter';
import { ToolHeader } from '../components/ToolHeader';

/**
 * `/tools/format/xml` — XML formatter / minifier.
 *
 * Pure two-mode tool (format / minify) on top of `<DualPaneFormatter>`.
 * Strict XML — unclosed tags are flagged with line/column markers and
 * surfaced in the error footer; HTML compatibility is parked at P1.
 */

type Mode = 'format' | 'minify';

const PLACEHOLDER = `粘贴 XML 文本…
例如：<root>
  <item id="1">hello</item>
  <item id="2">world</item>
</root>`;

export default function XmlTool(): JSX.Element {
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<Mode>('format');
  const [indent, setIndent] = useState<2 | 4>(2);

  const result: XmlResult = useMemo(() => {
    if (mode === 'format') return formatXml(input, { indent });
    return minifyXml(input);
  }, [input, mode, indent]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-4">
      <ToolHeader
        toolId="xml"
        category="format"
        description="XML 美化 / 压缩，含闭合校验。CDATA / 注释 / 命名空间前缀全部保留，错误位置精确到行列号。"
      />
      <DualPaneFormatter
        language="xml"
        inputValue={input}
        onInputChange={setInput}
        inputPlaceholder={PLACEHOLDER}
        outputValue={result.ok ? result.output : ''}
        outputFilename="output.xml"
        error={
          result.ok
            ? undefined
            : { message: result.error, line: result.line, column: result.column }
        }
        toolbar={
          <>
            <div className="inline-flex rounded-md border border-line">
              <ModeButton current={mode} value="format" onClick={setMode}>
                <Sparkles className="h-3.5 w-3.5" />
                美化
              </ModeButton>
              <ModeButton current={mode} value="minify" onClick={setMode}>
                <Minimize2 className="h-3.5 w-3.5" />
                压缩
              </ModeButton>
            </div>
            <label className="flex items-center gap-1 text-xs text-ink-muted">
              <span>缩进</span>
              <select
                className="select py-0.5"
                value={indent}
                onChange={(e) => setIndent(Number(e.target.value) as 2 | 4)}
                disabled={mode !== 'format'}
                title="美化缩进空格数（压缩模式不生效）"
              >
                <option value={2}>2</option>
                <option value={4}>4</option>
              </select>
            </label>
          </>
        }
      />
    </div>
  );
}

function ModeButton({
  current,
  value,
  onClick,
  children,
}: {
  current: Mode;
  value: Mode;
  onClick: (m: Mode) => void;
  children: React.ReactNode;
}): JSX.Element {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={clsx(
        'inline-flex items-center gap-1 px-2.5 py-0.5 text-[11.5px] font-medium transition first:rounded-l-md last:rounded-r-md',
        active ? 'bg-accent/10 text-accent' : 'text-ink-muted hover:bg-bg-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
